// Outils sur le texte Markdown source (avant rendu)

const FENCE_RE = /^ {0,3}(`{3,}|~{3,})/;
const HEADING_RE = /^ {0,3}(#{1,6})(?:\s|$)/;

// ── Tampon du streaming ──────────────────────────────────────────────
// Blocs suivis sur les lignes complètes. Toute indentation est admise : un bloc de code
// ou une formule dans un élément de liste est indenté de 2 à 6 espaces.
const FENCE_OPEN_RE = /^([ \t]*)(`{3,}|~{3,})(.*)$/;
const FENCE_CLOSE_RE = /^[ \t]*(`{3,}|~{3,})[ \t]*$/;
const MATH_OPEN_RE = /^([ \t]*)(\${2,})([^$]*)$/;
const MATH_CLOSE_RE = /^[ \t]*(\${2,})[ \t]*$/;
// Début d'un autre bloc : fin d'un tableau en cours
const BLOCK_START_RE = /^[ \t]*(?:#{1,6}(?:[ \t]|$)|>|[-*+](?:[ \t]|$)|\d{1,9}[.)](?:[ \t]|$))/;
// Ligne de séparation d'un tableau (« | --- | :--: | »), et ce qui peut encore en devenir une
const DELIMITER_ROW_RE = /^[ \t]*\|?[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/;
const DELIMITER_PREFIX_RE = /^[ \t|:-]*$/;
// Ligne faite de marqueurs seuls : filet, soulignement de titre, puce ou titre vides,
// début de bloc de code ou de formule, citation vide, ligne de séparation commencée
const MARKERS_ONLY_RE = /^[-*+_=#>`~$|:\s]+$/;
// Début d'encadré « [!NOTE] » ou de case à cocher « [ ] » / « [x] » encore incomplets
const CALLOUT_PREFIX_RE = /^\[(?:!(?:[a-z]*\]?)?)?[ \t]*$/i;
const TASK_PREFIX_RE = /^\[(?:[ xX](?:\][ \t]*)?)?$/;

const startsWithPipe = (line) => /^[ \t]*\|/.test(line);
const hasPipe = (line) => /(^|[^\\])\|/.test(line);
const isBlank = (line) => !line.trim();

// Nombre de cellules d'une ligne de tableau (barres d'extrémité et « \| » non comptées)
function cellCount(line) {
  const inner = line.trim().replace(/^\|/, '').replace(/(^|[^\\])\|$/, '$1');
  return inner.split(/(?<!\\)\|/).length;
}

const isDelimiterRow = (line) => line.includes('|') && DELIMITER_ROW_RE.test(line);

// Définition de lien ou de note (« [x]: url », « [^1]: … ») encore possible : elle
// disparaîtrait du texte (ou partirait en bas de page) à l'arrivée du « ]: »
const mayBeDefinition = (s) => /^\[[^\]]*(\](:.*)?)?$/.test(s);

/**
 * Dernière ligne incomplète qui peut encore changer la structure : « - » (liste, filet
 * ou titre souligné), « 1 » / « 1. » (liste ou nombre), « # », « > [!NO », « | … » hors
 * d'un tableau confirmé, « ``` » et sa langue, « $$ », « < », « [ … ». Une fois levée
 * (« - a », « 1.5 litres »), l'ambiguïté ne revient pas avec les caractères suivants.
 */
export function isAmbiguousLine(line, { inTable = false } = {}) {
  const s = line.replace(/^[ \t]+/, '');
  if (!s || MARKERS_ONLY_RE.test(s)) return true;
  if (s[0] === '|') return !inTable;
  if (s[0] === '<' || s.startsWith('$$') || /^(`{3,}|~{3,})/.test(s)) return true;
  if (s[0] === '[') return mayBeDefinition(s);
  let m = /^>[ \t]?(.*)$/.exec(s);
  if (m) return CALLOUT_PREFIX_RE.test(m[1]) || isAmbiguousLine(m[1]);
  m = /^[-*+][ \t]+(.*)$/.exec(s);
  if (m) return TASK_PREFIX_RE.test(m[1]) || isAmbiguousLine(m[1]);
  if (/^\d{1,9}[.)]?$/.test(s)) return true;
  m = /^\d{1,9}[.)][ \t]+(.*)$/.exec(s);
  if (m) return isAmbiguousLine(m[1]);
  return false;
}

/**
 * Texte rendu pendant le streaming, pour qu'aucun élément ne change de nature d'un
 * rendu partiel au suivant (paragraphe → tableau, titre → liste) :
 *  - la dernière ligne incomplète est retenue tant qu'elle peut changer la structure
 *    (isAmbiguousLine) ;
 *  - l'en-tête d'un tableau (« | a | b | ») est retenu jusqu'à sa ligne de séparation ;
 *  - un bloc de code ``` ou ~~~ ouvert est fermé provisoirement, à son indentation ;
 *  - une formule $$ ouverte est fermée provisoirement, sans son contenu partiel, et
 *    signalée (pendingMath) : le rendu affiche à sa place un espace réservé neutre,
 *    jamais l'erreur KaTeX d'une formule incomplète.
 * Le texte rendu n'est qu'un préfixe du message (plus les fermetures provisoires) : les
 * positions des éléments déjà affichés ne changent pas.
 * @returns {{source: string, pendingMath: boolean}}
 */
export function streamingMarkdown(content) {
  const lines = String(content ?? '').split('\n');
  const tail = lines.pop();
  let fence = null; // {mark, indent}
  let math = null; // {mark, indent, line}
  let inTable = false; // tableau confirmé par sa ligne de séparation
  let header = -1; // ligne d'en-tête possible d'un tableau pas encore confirmé

  lines.forEach((line, i) => {
    if (fence) {
      const close = FENCE_CLOSE_RE.exec(line);
      if (close && close[1][0] === fence.mark[0] && close[1].length >= fence.mark.length) fence = null;
      return;
    }
    if (math) {
      const close = MATH_CLOSE_RE.exec(line);
      if (close && close[1].length >= math.mark.length) math = null;
      return;
    }
    const fenceOpen = FENCE_OPEN_RE.exec(line);
    // « ``` » suivi d'un autre accent grave sur la même ligne : du code en ligne
    if (fenceOpen && !(fenceOpen[2][0] === '`' && fenceOpen[3].includes('`'))) {
      fence = { mark: fenceOpen[2], indent: fenceOpen[1] };
      inTable = false;
      header = -1;
      return;
    }
    const mathOpen = MATH_OPEN_RE.exec(line);
    if (mathOpen) {
      math = { mark: mathOpen[2], indent: mathOpen[1], line: i };
      inTable = false;
      header = -1;
      return;
    }
    if (isBlank(line) || (inTable && BLOCK_START_RE.test(line))) {
      inTable = false;
      header = -1;
      return;
    }
    if (inTable) return;
    if (header >= 0 && isDelimiterRow(line) && cellCount(line) === cellCount(lines[header])) {
      inTable = true;
      header = -1;
      return;
    }
    header = hasPipe(line) ? i : -1;
  });

  // Formule ouverte : tout ce qui suit son ouverture lui appartient
  if (math) {
    return { source: `${lines.slice(0, math.line + 1).join('\n')}\n${math.indent}${math.mark}`, pendingMath: true };
  }

  let shown = lines.length;
  let keepTail;
  if (fence) {
    // Dans un bloc de code, la dernière ligne est du code, sauf début de sa fermeture
    const close = /^[ \t]*(`+|~+)[ \t]*$/.exec(tail);
    keepTail = !(close && close[1][0] === fence.mark[0]);
  } else {
    keepTail = !isAmbiguousLine(tail, { inTable });
    // En-tête de tableau en attente de sa ligne de séparation
    if (header === lines.length - 1 && startsWithPipe(lines[header]) && DELIMITER_PREFIX_RE.test(tail)) {
      shown = header;
      keepTail = false;
    }
  }

  const kept = lines.slice(0, shown);
  if (keepTail) kept.push(tail);
  else if (shown) kept.push('');
  let source = kept.join('\n');
  if (fence) source += `${source.endsWith('\n') ? '' : '\n'}${fence.indent}${fence.mark}`;
  return { source, pendingMath: false };
}

/**
 * Markdown de la section qui commence au titre situé à `startOffset` :
 * jusqu'au prochain titre de niveau égal ou supérieur (hors blocs de code).
 */
export function sectionMarkdown(source, startOffset, depth) {
  const rest = source.slice(startOffset);
  const lines = rest.split('\n');
  let offset = lines[0].length + 1;
  let fence = null;

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const fenceMatch = FENCE_RE.exec(line);
    if (fenceMatch) {
      if (!fence) fence = fenceMatch[1];
      else if (fenceMatch[1][0] === fence[0] && fenceMatch[1].length >= fence.length) fence = null;
    } else if (!fence) {
      const heading = HEADING_RE.exec(line);
      if (heading && heading[1].length <= depth) break;
    }
    offset += line.length + 1;
  }

  return rest.slice(0, offset).trim();
}
